import React, { useCallback, useLayoutEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { type SafeAreaViewProps } from 'react-native-safe-area-context';
import {
  type RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { theme } from '@sg/garnish';

import { StripeCustomerSheet } from '@order/integrations/stripe';

import { PageWrapper, SignedOutView } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import type {
  ModalStackParamList,
  PaymentsScreenProps,
} from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const PaymentsScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const navigation = useNavigation<PaymentsScreenProps['navigation']>();
  const { params } = useRoute<RouteProp<ModalStackParamList, 'Payments'>>();
  const redirectTo = params?.redirectTo;

  // ─────────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation.navigate('ScanAtCheckout');
  }, [navigation]);

  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: true, presentation: 'modal' });
  }, [navigation]);

  if (!isLoggedIn) {
    return <SignedOutView redirectTo="payments" withoutFooter />;
  }

  return (
    <PageWrapper
      safeAreaEdges={safeAreaAndroid}
      style={styles.container}
      showsVerticalScrollIndicator={false}
      withHeaderTitle
      withoutFooter
    >
      <StripeCustomerSheet
        visible
        redirectTo={redirectTo}
        onClose={handleClose}
      />
    </PageWrapper>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const safeAreaAndroid: SafeAreaViewProps['edges'] = Platform.select({
  android: ['top'],
  default: [],
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['4'],
    gap: theme.spacing['4'],
  },
});
